// Baselayer Results Explorer — Addresses tab. Table of addresses with a
// deliverable badge, a type derived from the RDI (Residential/Commercial),
// and source chips.

import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  StatusBadge,
  EmptyState,
} from "@/design-system/components/ui";
import { Chips } from "../tabHelpers";
import {
  asArray,
  asBool,
  asObject,
  asString,
  pick,
  type ParsedKyb,
} from "../parse";

export function AddressesTab({ kyb }: { kyb: ParsedKyb }) {
  const b = kyb.business;
  const raw = asArray(
    pick(b, "addresses") ??
      pick(kyb.raw, "addresses") ??
      (pick(b, "search_address") ? [pick(b, "search_address")] : []),
  );

  const addresses = raw.map((a) => {
    const o = asObject(a);
    return {
      address: asString(pick(o, "full_address", "address", "formatted")) ?? "—",
      deliverable: asBool(pick(o, "deliverable")),
      rdi: asString(pick(o, "rdi", "residential_delivery_indicator")),
      sources: asArray(pick(o, "sources")).map((s) =>
        asString(s) ?? asString(pick(asObject(s), "name", "source")),
      ),
    };
  });

  if (addresses.length === 0) {
    return (
      <EmptyState
        title="No addresses"
        description="No addresses were returned for this subject."
      />
    );
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>Address</TH>
          <TH>Deliverable</TH>
          <TH>Type</TH>
          <TH>Sources</TH>
        </TR>
      </THead>
      <TBody>
        {addresses.map((a, i) => (
          <TR key={`${a.address}-${i}`}>
            <TD>{a.address}</TD>
            <TD>
              {a.deliverable == null ? (
                "—"
              ) : (
                <StatusBadge tone={a.deliverable ? "ok" : "danger"}>
                  {a.deliverable ? "Deliverable" : "Undeliverable"}
                </StatusBadge>
              )}
            </TD>
            <TD>{rdiLabel(a.rdi)}</TD>
            <TD>
              <Chips values={a.sources} />
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

function rdiLabel(rdi: string | null): string {
  if (!rdi) return "—";
  const v = rdi.toLowerCase();
  if (v.startsWith("res")) return "Residential";
  if (v.startsWith("com")) return "Commercial";
  return rdi;
}
